import type { Field } from '@lighthouse/core'
import { useDeferredValue, useMemo } from 'react'

import { useApplicationContext, useFieldBlockContext } from '../../../../contexts'
import { useAtomData } from '../../../../hooks'
import { fieldConvertValue, selectRecordFromPool } from '../../../../utils'

/**
 * 从 recordPoolAtom 订阅单个 record 的数据（使用 Map O(1) 查找）
 */
export const useRecordData = (recordId: string, dsId: string, relativeField: Field | undefined, relativeShowField: Field | undefined) => {
    const { recordPoolAtom } = useFieldBlockContext()
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()

    // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
    const selector = useMemo(() => selectRecordFromPool(dsId, recordId), [dsId, recordId])
    const record = useDeferredValue(useAtomData(recordPoolAtom, selector))

    // 获取显示字段值
    const displayValue = useMemo(() => {
        if (!record || !relativeShowField) {
            return ''
        }
        const fieldValue = record.content?.[relativeShowField.id]?.value
        if (!fieldValue) {
            return ''
        }
        return fieldConvertValue({ ...relativeShowField, value: fieldValue } as Parameters<typeof fieldConvertValue>[0], {
            personOptions,
            roleOptions,
            departmentOptions
        })
    }, [record, relativeShowField, personOptions, roleOptions, departmentOptions])

    // 获取字段的原始值（用于作为 value）
    const fieldRawValue = useMemo(() => {
        if (!record || !relativeField) {
            return ''
        }
        const fieldValue = record.content?.[relativeField.id]?.value
        if (!fieldValue) {
            return ''
        }
        return String(relativeField)
    }, [record, relativeField])

    return {
        record,
        displayValue,
        fieldRawValue,
        disabled: record?.disabled ?? false
    }
}
